import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { getRolePermissions } from "@/shared/permissions";
import { ApplicationConfigDto, LoggedUser, UserProfile } from "@/sharedTypes/commonTypes";
import { jwtDecode } from "jwt-decode";
import dayjs from "dayjs";
import { defaultRouteMappings, defaultRoutePropertyMappings } from "@/routes/CustomRouteMappings";
import { DashboardNotificationGridDto } from "@/components/Dashboard/Notifications/types";
import { FeatureFlags } from "./featureFlags";

export type RouteMapping = {
	// route is either mapped with a value of string or disabled with a value of null
	[key: string]: string | null;
};

export const collapsedAtom = atom<boolean>(false);

export const routeMappingAtom = atom<RouteMapping | null>(defaultRouteMappings);

export const routePropertyMappingAtom = atom<RouteMapping>(defaultRoutePropertyMappings);

export const rolePermissionsAtom = atom<Record<string, Array<string>>>(get => getRolePermissions());

export const profileDataAtom = atomWithStorage<UserProfile>("profileData", JSON.parse(localStorage.getItem("profileData") ?? "{}"));

export const columnsStateAtom = atomWithStorage<{ columnsState: Record<string, any> }>(
	"columnsState",
	JSON.parse(localStorage.getItem("columnsState") ?? "{}")
);

export const notificationsAtom = atom<DashboardNotificationGridDto[]>([]);

//create a primitive atom with base value of whatever
const loggedUserPrimitiveAtom = atom(localStorage.getItem("loggedUser"));
// then create a read write atom that will set and parse values
// if working with atoms and storing values through storage, always use storage logic in atoms and call setter functions through useAtom
// so the atom is rehydrated
export const loggedUserAtom = atom(
	get => {
		const jwtToken = get(loggedUserPrimitiveAtom);
		return jwtToken ? decodeJwt(jwtToken) : null;
	},
	// setLoggedUser to null if we want to remove jwt from storage
	(get, set, jwtToken: string) => {
		set(loggedUserPrimitiveAtom, jwtToken);
		jwtToken ? localStorage.setItem("loggedUser", jwtToken) : localStorage.removeItem("loggedUser");
	}
);

const applicationConfigPrimitiveAtom = atom(localStorage.getItem("applicationConfig"));
export const applicationConfigAtom = atom(
	get => JSON.parse(get(applicationConfigPrimitiveAtom) ?? "{}"),
	(get, set, config: ApplicationConfigDto) => {
		const jsonConfig = JSON.stringify(config);
		set(applicationConfigPrimitiveAtom, jsonConfig);
		localStorage.setItem("applicationConfig", jsonConfig);
	}
);

export const getEnabledFeatures = (): Array<string> => {
	let features: Array<string> = [];
	const enabledFeatures = localStorage.getItem("enabled-features");
	if (enabledFeatures) {
		features = JSON.parse(enabledFeatures);
	}

	return features;
};

export const isFeatureEnabled = (featureFlag: FeatureFlags): boolean => getEnabledFeatures().some(f => f === featureFlag);

export const decodeJwt = (jwtToken: string): LoggedUser => {
	const jwtData = jwtDecode<LoggedUser>(jwtToken);
	const roleNames = jwtData["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
	const roleNamesAsArray = Array.isArray(roleNames) ? roleNames : [roleNames];
	const user: LoggedUser = {
		id: jwtData["sub"],
		username: jwtData["unique_name"],
		fullName: `${jwtData["given_name"]} ${jwtData["family_name"]}`,
		roles: roleNamesAsArray,
		birthdate: dayjs(jwtData["birthdate"]),
		email: jwtData["email"],
		phoneNumber: jwtData["phone_number"],
		applicationVersion: jwtData["app-version"],
		workplaceTitle: jwtData["workplace-title"],
		employeeId: jwtData["employee-id"],
		branchOfficeId: jwtData["branch-office-id"]
	};

	return user;
};
